"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { BASE_URL, CDN_URL } from "../config/apiconfig";

/* ---------------- CONTEXT ---------------- */

const CategoriesContext = createContext(null);

export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error("useCategories must be used within CategoriesProvider");
  }
  return context;
};

const normalizeCategory = (c) => ({
  id: String(c.id),
  title: c.name || "",
  subtitle: c.description || "",
  image: c.image
    ? c.image.startsWith("uploads/")
      ? `${CDN_URL}/${c.image}`
      : `${CDN_URL}/${c.image}`
    : null,
  status: c.status ?? "Inactive",
});

/* ---------------- PROVIDER ---------------- */

export const CategoriesProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const CATEGORY_ENDPOINT = `${BASE_URL}/categories`;

  /* ---------- FETCH CATEGORIES ---------- */
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(CATEGORY_ENDPOINT);

      if (!res.ok) {
        throw new Error(`Failed to fetch categories: ${res.status}`);
      }

      const json = await res.json();
      const data = Array.isArray(json?.data) ? json.data : [];

      const processedCategories = data
        .map(normalizeCategory)
        .filter((c) => c.status === "Active");

      setCategories(processedCategories);


    } catch (err) {
      setCategories([]);
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------- AUTO FETCH ---------- */
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <CategoriesContext.Provider
      value={{
        categories,
        loading,
        error,
        refetch: fetchCategories,
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};
